import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { BASE as API_BASE } from '../services/api';
import { Button } from '../components/ui';
import { toast } from '../utils/toastBus';
import { AuthContext } from '../context/AuthContext';
import StreamCard from '../components/streams/StreamCard';
import { AudioPlayerProvider } from '../context/AudioPlayerContext';
import AdCard from '../components/AdCard';
import styles from './StreamDetail.module.css';

export default function StreamDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [genBusy, setGenBusy] = useState(false);
  const [videoBusy, setVideoBusy] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [predId, setPredId] = useState('');
  const [predStatus, setPredStatus] = useState('');
  const [showCoverFull, setShowCoverFull] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [adsByStream, setAdsByStream] = useState([]);

  const replicateEnabled = useMemo(() => {
    const raw = process.env.REACT_APP_REPLICATE_VIDEO_ENABLED;
    if (typeof raw === 'string') {
      const normalized = raw.trim().toLowerCase();
      if (['false', '0', 'off', 'disabled', 'no'].includes(normalized)) return false;
    }
    return true;
  }, []);

  const allowedVideoModes = useMemo(() => (
    replicateEnabled ? ['static', 'motion', 'animated'] : ['static', 'motion']
  ), [replicateEnabled]);

  const [videoMode, setVideoMode] = useState('static');

  useEffect(() => {
    if (!allowedVideoModes.includes(videoMode)) {
      setVideoMode(allowedVideoModes[0]);
    }
  }, [allowedVideoModes, videoMode]);

  const videoModeOptions = useMemo(() => allowedVideoModes.map((mode) => ({
    value: mode,
    label: mode === 'static'
      ? 'Static (image + audio)'
      : (mode === 'motion' ? 'Motion (image → video)' : 'Animated (prompt → video)'),
  })), [allowedVideoModes]);

  const demoMeta = useMemo(() => {
    try {
      const rawId = String(id || '');
      if (!rawId) return null;
      const demoPrefix = 'demo_';
      const demoExtRegex = /\.(mp3|wav|m4a|aac|flac|ogg|oga|webm|mp4)$/i;
      const isDemo = rawId.startsWith(demoPrefix) || demoExtRegex.test(rawId);
      if (!isDemo) return null;
      const filename = rawId.startsWith(demoPrefix) ? rawId.slice(demoPrefix.length) : rawId;
      if (!filename) return null;
      const sanitized = filename.replace(/^\/+/,'');
      const titleBase = sanitized.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ').trim() || 'Demo Stream';
      const audioPath = sanitized.startsWith('uploads/') ? `/${sanitized}` : `/uploads/${sanitized}`;
      return { filename: sanitized, title: titleBase, audioPath };
    } catch {
      return null;
    }
  }, [id]);

  const isDemoView = !!demoMeta;
  const isOwner = !isDemoView && item?.createdBy && user?._id && String(item.createdBy) === String(user._id);
  const isAdmin = !isDemoView && Array.isArray(user?.roles) && user.roles.includes('admin');

  useEffect(() => {
    (async () => {
      try {
        if (demoMeta) {
          setItem({
            _id: id,
            id,
            title: demoMeta.title,
            audioPath: demoMeta.audioPath,
            imageUrl: null,
            scriptText: '',
            visibility: 'public',
            moderationStatus: 'approved',
            demo: true,
          });
          setLoading(false);
          return;
        }
        setLoading(true);
        const res = await api.getStream(id);
        if (!res?.item) throw new Error('not_found');
        setItem(res.item);
      } catch (e) {
        toast({ type: 'error', message: 'Stream not found' });
        navigate('/discover');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate, demoMeta]);

  // Fetch ad videos created from this stream
  useEffect(() => {
    (async () => {
      if (demoMeta) return;
      try {
        const r = await api.getAdVideosByStream(id);
        setAdsByStream(Array.isArray(r?.items) ? r.items : []);
      } catch {}
    })();
  }, [id, demoMeta]);

  async function onClaimStream() {
    try {
      if (!item?._id) return;
      if (!user?._id) { toast({ type: 'info', message: 'Sign in to claim' }); return; }
      setClaiming(true);
      const res = await api.claimStream(item._id);
      if (res?.item) {
        setItem(res.item);
        toast({ type: 'success', message: 'Stream claimed' });
      } else if (res?.error === 'already_claimed') {
        toast({ type: 'info', message: 'Already claimed' });
      } else {
        toast({ type: 'error', message: 'Failed to claim stream' });
      }
    } catch {
      toast({ type: 'error', message: 'Failed to claim stream' });
    } finally {
      setClaiming(false);
    }
  }

  async function onGenerateCover() {
    try {
      if (!item) return;
      if (item?.imageUrl) {
        const ok = window.confirm('Are you sure? This will overwrite the current cover.');
        if (!ok) return;
      }
      setGenBusy(true);
      const title = String(item.title || '').trim();
      const excerpt = String(item.scriptText || '').trim().slice(0, 240);
      const prompt = title && excerpt
        ? `High-contrast minimal cover art for stream "${title}". Theme: ${excerpt}`
        : (title ? `High-contrast minimal cover art for stream "${title}"` : `High-contrast minimal cover art`);
      const aspect_ratio = '1:1';
      const gen = await api.generateImage({ prompt, aspect_ratio });
      const url = gen?.item?.url;
      if (!url) throw new Error(gen?.error || 'generate_failed');
      const upd = await api.updateStreamImage(id, { imageUrl: url });
      if (!upd?.item) throw new Error('failed_to_update_image');
      setItem(upd.item);
      toast({ type: 'success', message: 'Cover updated' });
    } catch (e) {
      toast({ type: 'error', message: e?.message || 'Failed to generate cover' });
    } finally {
      setGenBusy(false);
    }
  }

  // Inject social meta tags for sharing (Open Graph / Twitter)
  useEffect(() => {
    try {
      if (!item?._id) return; // guard until data is available
      const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
      const pageUrl = origin ? `${origin}/streams/${encodeURIComponent(String(item._id))}` : '';
      const title = String(item?.title || 'Listen on Loudest.ai');
      const description = String((item?.scriptText || '').slice(0, 240));
      const rawImg = String(item?.imageUrl || '');
      const imageAbs = rawImg ? (rawImg.startsWith('/uploads/') ? `${API_BASE}${rawImg}` : rawImg) : '';

      const ensureMeta = (name, content, attr = 'property') => {
        if (!content) return;
        let el = document.head.querySelector(`meta[${attr}="${name}"]`);
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute(attr, name);
          document.head.appendChild(el);
        }
        el.setAttribute('content', content);
      };

      ensureMeta('og:type', 'article');
      if (pageUrl) ensureMeta('og:url', pageUrl);
      ensureMeta('og:title', title);
      if (description) ensureMeta('og:description', description);
      if (imageAbs) ensureMeta('og:image', imageAbs);

      ensureMeta('twitter:card', imageAbs ? 'summary_large_image' : 'summary', 'name');
      ensureMeta('twitter:title', title, 'name');
      if (description) ensureMeta('twitter:description', description, 'name');
      if (imageAbs) ensureMeta('twitter:image', imageAbs, 'name');
    } catch {}
  }, [item?._id, item?.title, item?.scriptText, item?.imageUrl]);

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!item) return null;

  const playUrl = item.audioPath && (String(item.audioPath).startsWith('/uploads/') ? `${API_BASE}${item.audioPath}` : item.audioPath);

  return (
    <AudioPlayerProvider>
    <div className={styles.grid}>
      <div>
        <StreamCard
          stream={item}
          showActions={{ like: !!item._id, play: true, share: true, delete: !!(isAdmin && item._id) }}
          onLike={async () => {
            try { await api.likeStream(item._id); } catch {}
          }}
          onShare={() => {}}
          onDelete={async () => {
            try {
              if (!window.confirm('Delete this stream?')) return;
              await api.deleteStream(item._id);
              toast({ type: 'success', message: 'Stream deleted' });
              navigate('/discover');
            } catch (err) { toast({ type: 'error', message: err?.message || 'Failed to delete stream' }); }
          }}
        />
        {!isDemoView && !item.createdBy && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span style={{ background: '#1f2937', border: '1px solid #374151', color: '#eab308', padding: '4px 8px', borderRadius: 6, fontSize: 12 }}>Unclaimed</span>
            {user?._id && (
              <Button onClick={onClaimStream} disabled={claiming} size="sm" style={{ background: '#374151', color: '#e5e7eb' }}>
                {claiming ? 'Claiming…' : 'Claim Stream'}
              </Button>
            )}
          </div>
        )}
        {(isOwner || isAdmin) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <Button disabled={genBusy} onClick={onGenerateCover} style={{ background: '#ff5a1f', color: '#fff' }}>
              {genBusy ? 'Generating…' : (item?.imageUrl ? 'Regenerate Cover' : 'Generate Cover')}
            </Button>
            {(!item?.videoUrl && item?.imageUrl && item?.audioPath) && (
              <Button
                disabled={videoBusy}
                onClick={async () => {
                  try {
                    if (!allowedVideoModes.includes(videoMode)) {
                      toast({ type: 'error', message: 'Unsupported mode' });
                      return;
                    }
                    setVideoBusy(true);
                    setVideoUrl('');
                    // Async start/status/finalize
                    const start = await api.adVideoStart({
                      streamId: item._id,
                      imageUrl: item.imageUrl,
                      audioPath: item.audioPath,
                      durationSec: Number(item?.durationSec || 30),
                      aspectRatio: '16:9',
                      prompt: String(item?.scriptText || ''),
                      mode: videoMode,
                    });
                    if (start?.error || !start?.predictionId) {
                      throw new Error(start?.message || start?.error || 'video_start_failed');
                    }
                    setPredId(start.predictionId);
                    setPredStatus('starting');
                    let status = 'starting';
                    let tries = 0;
                    while (tries < 120) {
                      const st = await api.adVideoStatus(start.predictionId);
                      if (st?.error) {
                        throw new Error(st?.message || st.error);
                      }
                      status = st?.status || 'unknown';
                      setPredStatus(status);
                      if (status === 'succeeded' || status === 'failed' || status === 'canceled') break;
                      await new Promise(r => setTimeout(r, 2000));
                      tries++;
                    }
                    if (status !== 'succeeded') throw new Error(`video_status_${status || 'unknown'}`);
                    const fin = await api.adVideoFinalize({ predictionId: start.predictionId, streamId: item._id });
                    if (fin?.error) throw new Error(fin?.message || fin.error);
                    if (fin?.item) setItem(fin.item);
                    const url = fin?.item?.videoUrl;
                    if (url) { setVideoUrl(url); toast({ type: 'success', message: 'Video ready' }); }
                    else { toast({ type: 'warning', message: 'Finalized but no URL returned' }); }
                  } catch (e) {
                    toast({ type: 'error', message: e?.message || 'Create video failed' });
                  } finally {
                    setVideoBusy(false);
                  }
                }}
                style={{ background: '#374151', color: '#e5e7eb' }}
              >{videoBusy ? 'Creating…' : 'Create Video'}</Button>
            )}
          </div>
        )}
        {isDemoView && (
          <div style={{ marginTop: 8, color: '#9ca3af', fontSize: 12 }}>Demo stream preview</div>
        )}
        {!isDemoView && !(isOwner || isAdmin) && (
          <div style={{ marginTop: 8, color: '#9ca3af', fontSize: 12 }}>
            Generate Cover is available to the stream owner or admins.
          </div>
        )}
      </div>
      <div style={{ background: '#0f172a', border: '1px solid #374151', borderRadius: 12, padding: 16 }}>
        <h3 style={{ color: '#e5e7eb', marginTop: 0 }}>Script</h3>
        <pre style={{ whiteSpace: 'pre-wrap', color: '#d1d5db', fontFamily: 'inherit' }}>{item.scriptText || '—'}</pre>
        {(isOwner || isAdmin) && (!item?.videoUrl && item?.imageUrl && item?.audioPath) && (
          <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, marginTop: 8 }}>
            <select value={videoMode} onChange={(e) => setVideoMode(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #374151', background: '#0b0b0e', color: '#e5e7eb' }}>
              {videoModeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <div style={{ color: '#9ca3af', fontSize: 12, display: 'flex', alignItems: 'center' }}>
              {predId ? <>Prediction: {predId} • {predStatus}</> : 'Select mode and click Create Video above'}
            </div>
          </div>
        )}
        {(videoUrl || item?.videoUrl) && (
          <div style={{ marginTop: 12 }}>
            <h4 style={{ color: '#e5e7eb', margin: '12px 0 6px' }}>Video</h4>
            <video
              src={(videoUrl || item.videoUrl).startsWith('/uploads/') ? `${API_BASE}${videoUrl || item.videoUrl}` : (videoUrl || item.videoUrl)}
              controls
              preload="metadata"
              style={{ width: '100%', maxWidth: 640, borderRadius: 8, border: '1px solid #374151' }}
              poster={item?.imageUrl?.startsWith('/uploads/') ? `${API_BASE}${item.imageUrl}` : item?.imageUrl}
            />
          </div>
        )}

        {adsByStream.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h4 style={{ color: '#e5e7eb', margin: '12px 0 6px' }}>Created Ads</h4>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {adsByStream.map((ad) => (
                <AdCard key={ad._id} ad={ad} />
              ))}
            </div>
          </div>
        )}
      </div>

      {showCoverFull && item?.imageUrl && (() => {
        const raw = item.imageUrl || '';
        const directSrc = raw.startsWith('/uploads/') ? `${API_BASE}${raw}` : raw;
        const needsAltHost = /(^|\.)via\.placeholder\.com/i.test(directSrc);
        const altHostSrc = `https://placehold.co/1024x1024/png?text=${encodeURIComponent(String(item.title || 'Cover'))}`;
        const srcBase = needsAltHost ? altHostSrc : directSrc;
        const bust = `${srcBase}${srcBase.includes('?') ? '&' : '?'}t=${Date.now()}`;
        const fallbackSvg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='1200' height='1200'><rect width='100%' height='100%' fill='%23111'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%23ff7a45' font-family='Arial, Helvetica, sans-serif' font-size='64'>${String(item.title || 'Cover')}</text></svg>`)}`;
        return (
          <div onClick={() => setShowCoverFull(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <img
              src={bust}
              alt="Cover"
              style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.6)' }}
              crossOrigin="anonymous"
              referrerPolicy="no-referrer"
              onError={(e) => {
                try {
                  const el = e?.currentTarget;
                  if (!el) return;
                  if (el.dataset.fallback === 'svg') return;
                  if (!el.dataset.triedDirect) {
                    el.dataset.triedDirect = '1';
                    el.src = directSrc;
                    return;
                  }
                  if (!el.dataset.triedAlt) {
                    el.dataset.triedAlt = '1';
                    el.src = altHostSrc;
                    return;
                  }
                  el.dataset.fallback = 'svg';
                  el.removeAttribute('crossorigin');
                  el.removeAttribute('referrerpolicy');
                  el.src = fallbackSvg;
                } catch {}
              }}
            />
          </div>
        );
      })()}
    </div>
    </AudioPlayerProvider>
  );
}
